import { useCallback, useEffect, useState } from "react";
import {
  fetchAdminDiagnoses,
  type AdminDiagnosis,
} from "../api/adminApi";
import { fetchAuditorDiagnoses } from "../api/auditorApi";
import { getAuthToken, getStoredAccountType } from "../auth";
import { loadStandardDiagnosticCodes } from "../standardDiagnosticCodesStorage";
import type { StandardDiagnosticCode } from "../types";

function mapApiDiagnosis(row: AdminDiagnosis | Record<string, unknown>): StandardDiagnosticCode {
  const r = row as AdminDiagnosis;
  return {
    id: String(r.id),
    code: String(r.code ?? ""),
    label: String(r.label || r.name || ""),
    type: String(r.type || ""),
    description: String(r.description || ""),
  };
}

export function useAdminDiagnoses() {
  const [diagnostics, setDiagnostics] = useState<StandardDiagnosticCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setDiagnostics(loadStandardDiagnosticCodes());
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const isAuditor = getStoredAccountType() === "auditor";
      const api = isAuditor ? await fetchAuditorDiagnoses() : await fetchAdminDiagnoses();
      setDiagnostics((api.diagnoses ?? []).map(mapApiDiagnosis));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Impossible de charger les diagnostics standards.",
      );
      setDiagnostics(loadStandardDiagnosticCodes());
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    diagnostics,
    loading,
    error,
    usingApi,
    reload: load,
  };
}
